/**
 * Menu Renderer — orchestrates menu screens and ambient effects.
 *
 * Delegates to composition classes for each screen:
 *   TitleScreenRenderer    — title with orbiting shapes
 *   ModeSelectRenderer     — mode selection grid
 *   TankSelectRenderer     — SF2-style tank select
 *   LobbyRenderer          — multiplayer lobby
 *   PauseOverlayRenderer   — pause menu
 *   WeaponLabOverlayRenderer — training mode pause
 *
 * Parent keeps: ambient particles, animation systems, container orchestration,
 * tapVirtualKey utility, state ref, timing.
 */

import { Container, Graphics } from 'pixi.js';
import { Camera, Input } from 'bonkjs';
import type { GameState } from '../../types';
import type { VoidRenderer } from '../void';
import type { ParticleSystem } from '../particles';
import type { ScreenEffects } from '../effects';
import type { ScreenShake } from '../screen-shake';
import type { HUDRenderer } from '../hud';
import { WORLD, MENU, PLAYER_COLORS, Z } from '../../constants';
import type { MenuAnimDeps } from './types';
import { MenuVoidRiseAnimation } from './void-rise';
import { MenuTankConsumeAnimation } from './tank-consume';
import { MenuMortarAnimation } from './mortar';
import { TitleScreenRenderer } from './title-screen';
import { ModeSelectRenderer } from './mode-select';
import { TankSelectRenderer } from './tank-select';
import { StageSelectRenderer } from './stage-select';
import { LobbyRenderer } from './lobby';
import { PauseOverlayRenderer } from './pause-overlay';
import { WeaponLabOverlayRenderer } from './weapon-lab-overlay';
import { DisconnectOverlayRenderer } from './disconnect-overlay';
import { SkinsShopRenderer } from './skins-shop';
import { startPreviewGeneration, clearPreviews } from './terrain-preview';

// ─── Ambient particle ──
interface MenuParticle {
    gfx: Graphics;
    x: number;
    y: number;
    vy: number;
    life: number;
    maxLife: number;
    dark: boolean;
}

export class MenuRenderer {
    private container: Container;
    private uiContainer: Container;
    private worldContainer: Container;

    // Screen composition classes
    private titleScreen: TitleScreenRenderer;
    private modeSelect: ModeSelectRenderer;
    private tankSelect: TankSelectRenderer;
    private stageSelect: StageSelectRenderer;
    private lobby: LobbyRenderer;
    private pauseOverlay: PauseOverlayRenderer;
    private weaponLab: WeaponLabOverlayRenderer;
    private skinsShop: SkinsShopRenderer;
    private disconnectOverlay: DisconnectOverlayRenderer;

    // Screen containers (for visibility toggling)
    private titleContainer: Container;
    private modeContainer: Container;
    private tankSelectContainer: Container;
    private stageSelectContainer: Container;
    private skinsShopContainer: Container;
    private lobbyContainer: Container;

    // Ambient particles
    private particles: MenuParticle[] = [];
    private particleContainer: Container;

    // Animation systems
    private riseAnim: MenuVoidRiseAnimation;
    private consumeAnim: MenuTankConsumeAnimation;
    private mortarAnim: MenuMortarAnimation;

    // Game system refs
    private voidRenderer: VoidRenderer;
    private gameParticles: ParticleSystem;
    private effects: ScreenEffects;
    private camera: Camera;
    private screenShake: ScreenShake;
    private hud: HUDRenderer;

    // Timing
    private elapsed = 0;
    private wasMenu = false;
    private prevStageSelectVisible = false;

    // State ref for touch handlers
    private state: GameState | null = null;

    constructor(
        uiContainer: Container,
        worldContainer: Container,
        voidRenderer: VoidRenderer,
        gameParticles: ParticleSystem,
        effects: ScreenEffects,
        camera: Camera,
        screenShake: ScreenShake,
        hud: HUDRenderer,
    ) {
        this.uiContainer = uiContainer;
        this.worldContainer = worldContainer;
        this.voidRenderer = voidRenderer;
        this.gameParticles = gameParticles;
        this.effects = effects;
        this.camera = camera;
        this.screenShake = screenShake;
        this.hud = hud;

        // Root container
        this.container = new Container();
        this.container.zIndex = Z.MENU_UI;
        uiContainer.addChild(this.container);

        // Particle container
        this.particleContainer = new Container();
        this.container.addChild(this.particleContainer);

        // Closures for touch handlers
        const tap = (code: string) => this.tapVirtualKey(code);
        const getState = () => this.state;

        // --- Screen containers + composition classes ---

        this.titleContainer = new Container();
        this.container.addChild(this.titleContainer);
        this.titleScreen = new TitleScreenRenderer(this.titleContainer, tap);

        this.modeContainer = new Container();
        this.modeContainer.visible = false;
        this.container.addChild(this.modeContainer);
        this.modeSelect = new ModeSelectRenderer(this.modeContainer, tap, getState);

        this.tankSelectContainer = new Container();
        this.tankSelectContainer.visible = false;
        this.container.addChild(this.tankSelectContainer);
        this.tankSelect = new TankSelectRenderer(this.tankSelectContainer, tap, getState);

        this.skinsShopContainer = new Container();
        this.skinsShopContainer.visible = false;
        this.container.addChild(this.skinsShopContainer);
        this.skinsShop = new SkinsShopRenderer(this.skinsShopContainer, tap, getState);

        this.stageSelectContainer = new Container();
        this.stageSelectContainer.visible = false;
        this.container.addChild(this.stageSelectContainer);
        this.stageSelect = new StageSelectRenderer(this.stageSelectContainer, tap, getState);

        this.lobbyContainer = new Container();
        this.lobbyContainer.visible = false;
        this.container.addChild(this.lobbyContainer);
        this.lobby = new LobbyRenderer(this.lobbyContainer, tap, getState);

        this.pauseOverlay = new PauseOverlayRenderer(uiContainer, tap, getState);
        this.weaponLab = new WeaponLabOverlayRenderer(uiContainer);
        this.disconnectOverlay = new DisconnectOverlayRenderer(uiContainer, tap, getState);

        // Animation systems
        this.riseAnim = new MenuVoidRiseAnimation();
        this.consumeAnim = new MenuTankConsumeAnimation(worldContainer);
        this.mortarAnim = new MenuMortarAnimation(worldContainer);
    }

    // ─── Public API ────────────────────────────────────────

    update(state: GameState, dt: number): void {
        this.state = state;
        this.elapsed += dt;

        const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'skins_shop' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting';
        this.container.visible = isMenu;
        const isTrainingPause = state.paused && state.modeState.mode === 'training';
        this.pauseOverlay.setVisible(state.paused && !isTrainingPause);
        this.weaponLab.setVisible(isTrainingPause);

        // Hide all screen containers first, then show the active one
        this.titleContainer.visible = false;
        this.modeContainer.visible = false;
        this.skinsShopContainer.visible = false;
        this.tankSelectContainer.visible = false;
        this.stageSelectContainer.visible = false;
        this.lobbyContainer.visible = false;

        if (state.phase === 'title_screen') {
            this.titleContainer.visible = true;
            this.titleScreen.update(this.elapsed, dt);
        } else if (state.phase === 'mode_select') {
            this.modeContainer.visible = true;
            this.modeSelect.update(state);
        } else if (state.phase === 'skins_shop') {
            this.skinsShopContainer.visible = true;
            this.skinsShop.update(state, this.elapsed);
        } else if (state.phase === 'tank_select') {
            this.tankSelectContainer.visible = true;
            this.tankSelect.update(state, this.elapsed, dt);
        } else if (state.phase === 'stage_select') {
            this.stageSelectContainer.visible = true;
            // Start preview generation on entry
            if (!this.prevStageSelectVisible) {
                this.stageSelect.resetPreviews();
                startPreviewGeneration(state.randomPreviewSeed, state.randomPreviewThemeId);
            }
            this.stageSelect.update(state, this.elapsed);
        } else if (state.phase === 'mp_lobby' || state.phase === 'mp_waiting') {
            this.lobbyContainer.visible = true;
            this.lobby.update(state, this.elapsed);
        }

        // Track stage select visibility for preview lifecycle
        const stageSelectVisible = state.phase === 'stage_select';
        if (this.prevStageSelectVisible && !stageSelectVisible) {
            clearPreviews();
        }
        this.prevStageSelectVisible = stageSelectVisible;

        if (state.paused) {
            if (isTrainingPause) {
                this.weaponLab.update(state, this.elapsed);
            } else {
                this.pauseOverlay.update(state, this.elapsed);
            }
        }

        // Disconnect overlay — shows during any phase EXCEPT game_over
        // (post-match screen handles disconnect status inline)
        const showDisconnect = !!state.multiplayer?.disconnectPhase && state.phase !== 'game_over';
        this.disconnectOverlay.setVisible(showDisconnect);
        if (showDisconnect) this.disconnectOverlay.update(state, this.elapsed);

        // Ambient particles + animation systems
        // Skip during mp_waiting — resetGame() already ran and game state is live;
        // menu animations would overwrite voidY with stale animation values.
        const animMenu = isMenu && state.phase !== 'mp_waiting';
        if (animMenu) {
            this.updateParticles(dt, state);
            const deps: MenuAnimDeps = {
                voidRenderer: this.voidRenderer,
                gameParticles: this.gameParticles,
                effects: this.effects,
                camera: this.camera,
                screenShake: this.screenShake,
            };
            this.riseAnim.update(dt, state, deps);
            this.consumeAnim.update(dt, state, deps);
            this.mortarAnim.update(dt, state, deps);
            this.wasMenu = true;
        } else if (this.wasMenu) {
            this.consumeAnim.cleanup();
            this.mortarAnim.cleanup();
            this.riseAnim.reset();
            this.wasMenu = false;
        }
    }

    destroy(): void {
        this.consumeAnim.cleanup();
        this.mortarAnim.cleanup();
        for (const p of this.particles) {
            p.gfx.destroy();
        }
        this.particles = [];
        this.container.destroy({ children: true });
        this.pauseOverlay.destroy();
        this.weaponLab.destroy();
        this.disconnectOverlay.destroy();
        this.tankSelect.destroy();
    }

    // ─── Ambient Particles ───────────────────────────────

    private updateParticles(dt: number, state: GameState): void {
        if (this.particles.length < MENU.AMBIENT_MAX && Math.random() < MENU.AMBIENT_SPAWN_RATE) {
            this.spawnParticle(state);
        }

        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.life -= dt;

            if (p.life <= 0) {
                p.gfx.destroy();
                this.particles.splice(i, 1);
                continue;
            }

            p.y -= p.vy * dt;
            p.gfx.position.set(p.x, p.y);

            const lifeRatio = p.life / p.maxLife;
            const fadeIn = Math.min(1, (1 - lifeRatio) * 5);
            const fadeOut = Math.min(1, lifeRatio * 3);
            p.gfx.alpha = fadeIn * fadeOut * (p.dark ? 0.6 : 0.8);
        }
    }

    private spawnParticle(state: GameState): void {
        const isDark = Math.random() < MENU.AMBIENT_DARK_CHANCE;
        const color = isDark
            ? MENU.AMBIENT_DARK_COLOR
            : (Math.random() < 0.5 ? PLAYER_COLORS[0] : PLAYER_COLORS[1]);
        const radius = MENU.AMBIENT_RADIUS_MIN + Math.random() * (MENU.AMBIENT_RADIUS_MAX - MENU.AMBIENT_RADIUS_MIN);
        const life = MENU.AMBIENT_LIFE_MIN + Math.random() * (MENU.AMBIENT_LIFE_MAX - MENU.AMBIENT_LIFE_MIN);
        const vy = MENU.AMBIENT_SPEED_MIN + Math.random() * (MENU.AMBIENT_SPEED_MAX - MENU.AMBIENT_SPEED_MIN);

        const x = Math.random() * WORLD.CANVAS_WIDTH;
        const y = WORLD.CANVAS_HEIGHT - 40 + Math.random() * 20;

        const gfx = new Graphics();
        gfx.circle(0, 0, radius);
        if (isDark) {
            gfx.fill({ color, alpha: 0.6 });
        } else {
            gfx.fill({ color, alpha: 0.8 });
        }
        gfx.position.set(x, y);
        this.particleContainer.addChild(gfx);

        this.particles.push({ gfx, x, y, vy, life, maxLife: life, dark: isDark });
    }

    // ─── Touch Navigation ────────────────────────────────

    private tapVirtualKey(code: string): void {
        Input.setVirtualKey(code, true);
        setTimeout(() => Input.setVirtualKey(code, false), 67);
    }
}
